/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { dataPixels } from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import { calculateTextHeight } from './textMeasurements';
import calcRotatedResizeOffset from './calcRotatedResizeOffset';
import getTextHeightResizeUpdates from './getTextHeightResizeUpdates';

/**
 * Gets updated size and position of the element if relevant properties have changed.
 *
 * @param {Object} properties Updated element properties.
 * @param {Object} updatedProperties Submitted element properties.
 * @param {Object} oldProperties Original element properties.
 * @return {{x: number, y: number, height: number}} Updated values.
 */
function getUpdatedSizeAndPosition(
  properties,
  updatedProperties = {},
  oldProperties = {}
) {
  if (
    Object.keys(updatedProperties).length === 1 &&
    Object.prototype.hasOwnProperty.call(updatedProperties, 'verticalAnchor')
  ) {
    return {};
  }

  const { width, height: oldHeight, rotationAngle, x, y } = properties;
  const newHeight = dataPixels(calculateTextHeight(properties, width));
  const preserveCenter =
    properties.verticalAnchor !== 'bottom' &&
    properties.verticalAnchor !== 'center' &&
    Object.prototype.hasOwnProperty.call(updatedProperties, 'verticalTrim');

  if (!preserveCenter) {
    const updates = getTextHeightResizeUpdates(properties, newHeight);
    if (properties.verticalAnchor === 'center') {
      return updates;
    }
    return {
      height: dataPixels(updates.height),
      x: dataPixels(updates.x),
      y: dataPixels(updates.y),
    };
  }

  const heightDelta = newHeight - oldHeight;
  const oldCenterOffset = getTextCenterOffset({
    ...properties,
    ...oldProperties,
    height: oldProperties.height ?? oldHeight,
    verticalTrim:
      oldProperties.verticalTrim ?? getPreviousVerticalTrim(properties),
  });
  const newCenterOffset = getTextCenterOffset({
    ...properties,
    height: newHeight,
  });
  const topShift = preserveCenter ? oldCenterOffset - newCenterOffset : 0;
  const topDelta = preserveCenter ? -topShift : 0;
  const bottomDelta = heightDelta - topDelta;
  const [dx, dy] = calcRotatedResizeOffset(
    rotationAngle,
    0,
    0,
    topDelta,
    bottomDelta
  );
  return {
    height: newHeight,
    x: dataPixels(x + dx),
    y: dataPixels(y + dy),
  };
}

function getPreviousVerticalTrim(properties) {
  return properties.verticalTrim === 'baseline' ? 'none' : 'baseline';
}

function getTextCenterOffset({ font, fontSize, height, verticalTrim }) {
  const metrics = font?.metrics;
  if (
    !metrics ||
    !fontSize ||
    !metrics.upm ||
    metrics.asc === undefined ||
    metrics.capH === undefined ||
    metrics.des === undefined
  ) {
    return height / 2;
  }

  if (verticalTrim === 'baseline') {
    return height / 2;
  }

  const ascent = (fontSize * metrics.asc) / metrics.upm;
  const descent = (fontSize * Math.abs(metrics.des)) / metrics.upm;
  const capHeight = (fontSize * metrics.capH) / metrics.upm;
  return height / 2 + (ascent - capHeight - descent) / 2;
}

export default getUpdatedSizeAndPosition;
